"use client"

import * as React from "react"
import { Calendar } from "@/components/ui/calendar"
import { WeatherWidget } from "@/components/widgets/WeatherWidget"
import { CalendarApp } from "@/components/widgets/CalendarApp"
import { CalculatorWidget } from "@/components/widgets/CalculatorWidget"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import {
  ChevronDown,
  ChevronUp,
  Minus,
  X,
  PinOff,
  PanelLeft,
  PanelRight,
  PanelTop,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  GripHorizontal,
  Move
} from "lucide-react"

export type PanelSide = "left" | "right" | "top"

interface PinnedWidgetsPanelProps {
  side: PanelSide
  pinnedWidgets: string[]
  setPinnedWidgets?: React.Dispatch<React.SetStateAction<string[]>>
  widgetsConfig: any[]
  minimizedWidgets: Set<string>
  calcFocusedWidgetId: string | null
  setCalcFocusedWidgetId: (id: string | null) => void
  handleMinimizeWidget: (id: string) => void
  handlePinWidget: (id: string) => void
  handleToggleFloatingWidget: (id: string) => void
  calendarDate: Date | undefined
  setCalendarDate: (date: Date | undefined) => void
  weatherInfo: string
  lang?: string
  theme?: string
  calcFormula: string
  setCalcFormula: React.Dispatch<React.SetStateAction<string>>
  calcDisplay: string
  setCalcDisplay: React.Dispatch<React.SetStateAction<string>>
  showCalcHistory: boolean
  setShowCalcHistory: React.Dispatch<React.SetStateAction<boolean>>
  calcHistory: Array<{ expr: string; result: string }>
  setCalcHistory: React.Dispatch<React.SetStateAction<Array<{ expr: string; result: string }>>>
  setCalcResetOnNext: React.Dispatch<React.SetStateAction<boolean>>
  handleCalcPress: (val: string) => void
  notesContent: string
  setNotesContent: (content: string) => void
  pinnedWidgetSides: Record<string, PanelSide>
  setPinnedWidgetSides: React.Dispatch<React.SetStateAction<Record<string, PanelSide>>>
  collapsedSides: Record<PanelSide, boolean>
  setCollapsedSides: React.Dispatch<React.SetStateAction<Record<PanelSide, boolean>>>
  sideWidths: Record<"left" | "right", number>
  setSideWidths: React.Dispatch<React.SetStateAction<Record<"left" | "right", number>>>
  topPanelHeight: number
  setTopPanelHeight: React.Dispatch<React.SetStateAction<number>>
  showToast?: (title: string, desc: string, variant?: any) => void
}

export function PinnedWidgetsPanel({
  side,
  pinnedWidgets,
  setPinnedWidgets,
  widgetsConfig,
  minimizedWidgets,
  calcFocusedWidgetId,
  setCalcFocusedWidgetId,
  handleMinimizeWidget,
  handlePinWidget,
  handleToggleFloatingWidget,
  calendarDate,
  setCalendarDate,
  weatherInfo,
  lang = "pt",
  theme,
  calcFormula,
  setCalcFormula,
  calcDisplay,
  setCalcDisplay,
  showCalcHistory,
  setShowCalcHistory,
  calcHistory,
  setCalcHistory,
  setCalcResetOnNext,
  handleCalcPress,
  notesContent,
  setNotesContent,
  pinnedWidgetSides,
  setPinnedWidgetSides,
  collapsedSides,
  setCollapsedSides,
  sideWidths,
  setSideWidths,
  topPanelHeight,
  setTopPanelHeight,
  showToast
}: PinnedWidgetsPanelProps) {
  const [positionMenu, setPositionMenu] = React.useState<{ wid: string; x: number; y: number } | null>(null)

  const [widgetItemSizes, setWidgetItemSizes] = React.useState<Record<string, { height?: number; width?: number }>>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_pinned_widget_item_sizes")
        if (saved) return JSON.parse(saved)
      } catch {}
    }
    return {}
  })

  React.useEffect(() => {
    try {
      localStorage.setItem("bold_pinned_widget_item_sizes", JSON.stringify(widgetItemSizes))
    } catch {}
  }, [widgetItemSizes])

  React.useEffect(() => {
    const handleClickOutside = () => setPositionMenu(null)
    if (positionMenu) {
      window.addEventListener("click", handleClickOutside)
      return () => window.removeEventListener("click", handleClickOutside)
    }
  }, [positionMenu])

  const safeWidgetSides = pinnedWidgetSides || {}
  const safeCollapsedSides = collapsedSides || { left: false, right: false, top: false }
  const safeSideWidths = sideWidths || { left: 320, right: 320 }
  const safeTopPanelHeight = typeof topPanelHeight === "number" ? topPanelHeight : 180
  const safeLeftWidth = safeSideWidths.left || 320
  const safeRightWidth = safeSideWidths.right || 320

  const configuredSides = Object.values(safeWidgetSides)
  const defaultSide: PanelSide = configuredSides.length > 0 ? configuredSides[configuredSides.length - 1] : "right"

  const sideWidgets = (pinnedWidgets || []).filter((wid) => (safeWidgetSides[wid] || defaultSide) === side)

  if (sideWidgets.length === 0 || safeCollapsedSides[side]) return null

  // Helper to move a widget to a specific side (left, top, right)
  const moveWidgetToSide = (wid: string, targetSide: PanelSide) => {
    if (setPinnedWidgetSides) {
      setPinnedWidgetSides((prev) => ({ ...(prev || {}), [wid]: targetSide }))
    }
    if (setCollapsedSides) {
      setCollapsedSides((prev) => ({ ...(prev || { left: false, right: false, top: false }), [targetSide]: false }))
    }
    setPositionMenu(null)
  }

  const toggleCollapseSide = (s: PanelSide) => {
    if (setCollapsedSides) {
      setCollapsedSides((prev) => ({ ...(prev || { left: false, right: false, top: false }), [s]: !prev?.[s] }))
    }
  }

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const startX = e.clientX
    const startY = e.clientY
    const initialLeftW = safeLeftWidth
    const initialRightW = safeRightWidth
    const initialTopH = safeTopPanelHeight

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (side === "left") {
        const deltaX = moveEvent.clientX - startX
        const newW = Math.max(220, Math.min(600, initialLeftW + deltaX))
        if (setSideWidths) setSideWidths((prev) => ({ ...prev, left: newW }))
      } else if (side === "right") {
        const deltaX = startX - moveEvent.clientX
        const newW = Math.max(220, Math.min(600, initialRightW + deltaX))
        if (setSideWidths) setSideWidths((prev) => ({ ...prev, right: newW }))
      } else if (side === "top") {
        const deltaY = moveEvent.clientY - startY
        const newH = Math.max(140, Math.min(450, initialTopH + deltaY))
        if (setTopPanelHeight) setTopPanelHeight(newH)
      }
    }

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }

    document.body.style.userSelect = "none"
    document.body.style.cursor = side === "top" ? "row-resize" : "col-resize"
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)
  }

  const handleDragStartCard = (e: React.DragEvent, wid: string) => {
    e.dataTransfer.setData("text/plain", wid)
  }

  const handleDropCard = (e: React.DragEvent, targetWid: string) => {
    e.preventDefault()
    const draggedWid = e.dataTransfer.getData("text/plain")
    if (!draggedWid || draggedWid === targetWid || !setPinnedWidgets) return

    setPinnedWidgets((prev) => {
      const list = [...(prev || [])]
      const fromIdx = list.indexOf(draggedWid)
      const toIdx = list.indexOf(targetWid)
      if (fromIdx !== -1 && toIdx !== -1) {
        const [moved] = list.splice(fromIdx, 1)
        list.splice(toIdx, 0, moved)
      }
      return list
    })
  }

  const getWidgetDefaultSize = (wid: string, currentSide: PanelSide) => {
    if (currentSide === "top") {
      if (wid === "system-calculator") return { width: 340, minW: 280, maxW: 550 }
      if (wid === "system-calendar") return { width: 340, minW: 280, maxW: 500 }
      if (wid === "system-weather") return { width: 300, minW: 260, maxW: 450 }
      if (wid === "system-notes") return { width: 340, minW: 260, maxW: 600 }
      return { width: 320, minW: 260, maxW: 550 }
    } else {
      if (wid === "system-calculator") return { height: 420, minH: 300, maxH: 600 }
      if (wid === "system-calendar") return { height: 360, minH: 260, maxH: 550 }
      if (wid === "system-weather") return { height: 180, minH: 130, maxH: 350 }
      if (wid === "system-notes") return { height: 260, minH: 150, maxH: 550 }
      return { height: 300, minH: 150, maxH: 550 }
    }
  }

  const handleItemResizeStart = (e: React.MouseEvent, wid: string) => {
    e.preventDefault()
    e.stopPropagation()

    const startX = e.clientX
    const startY = e.clientY
    const def = getWidgetDefaultSize(wid, side)
    const currentW = widgetItemSizes[wid]?.width || def.width || 320
    const currentH = widgetItemSizes[wid]?.height || def.height || 300

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (side === "top") {
        const deltaX = moveEvent.clientX - startX
        const newW = Math.max(def.minW || 260, Math.min(def.maxW || 600, currentW + deltaX))
        setWidgetItemSizes((prev) => ({
          ...prev,
          [wid]: { ...prev[wid], width: newW }
        }))
      } else {
        const deltaY = moveEvent.clientY - startY
        const newH = Math.max(def.minH || 140, Math.min(def.maxH || 650, currentH + deltaY))
        setWidgetItemSizes((prev) => ({
          ...prev,
          [wid]: { ...prev[wid], height: newH }
        }))
      }
    }

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }

    document.body.style.userSelect = "none"
    document.body.style.cursor = side === "top" ? "col-resize" : "row-resize"
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)
  }

  const renderWidgetCardContent = (pw: any) => {
    const isMinimized = minimizedWidgets.has(pw.id)
    if (isMinimized) return null

    const isCalc = pw.id === "system-calculator"

    return (
      <div className={`flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden font-sans ${isCalc ? "p-0 scrollbar-none" : "p-2.5 pb-4 scrollbar-thin"}`}>
        {pw.id === "system-calendar" && (
          <div className="flex flex-col gap-2 items-center justify-center w-full h-full">
            <CalendarApp
              calendarDate={calendarDate}
              setCalendarDate={setCalendarDate}
              isWidget
            />
          </div>
        )}

        {pw.id === "system-weather" && (
          <div className="flex flex-col h-full justify-center">
            <WeatherWidget weatherInfo={weatherInfo} />
          </div>
        )}

        {pw.id === "system-calculator" && (
          <div className="flex-1 w-full h-full min-h-0 flex flex-col overflow-hidden">
            <CalculatorWidget
              calcFormula={calcFormula}
              setCalcFormula={setCalcFormula}
              calcDisplay={calcDisplay}
              setCalcDisplay={setCalcDisplay}
              showCalcHistory={showCalcHistory}
              setShowCalcHistory={setShowCalcHistory}
              calcHistory={calcHistory}
              setCalcHistory={setCalcHistory}
              setCalcResetOnNext={setCalcResetOnNext}
              handleCalcPress={handleCalcPress}
            />
          </div>
        )}

        {pw.id === "system-notes" && (
          <div className="flex flex-col gap-2 h-full">
            <textarea
              value={notesContent}
              onChange={(e) => setNotesContent(e.target.value)}
              placeholder="Escreva suas anotações..."
              className="w-full h-full min-h-[100px] p-3 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl text-xs text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#004EB1] resize-none font-sans"
            />
          </div>
        )}

        {pw.type === "developer" && pw.developerType === "iframe" && (
          <div className="w-full h-full rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950">
            <iframe
              src={pw.iframeUrl}
              data-widget-id={pw.id}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-downloads-without-user-activation"
              allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; downloads; downloads-without-user-activation"
              title={pw.name}
              onLoad={(e) => {
                try {
                  const currentTheme = (theme || (typeof window !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"
                  const currentLang = lang || (typeof window !== "undefined" ? localStorage.getItem("language") || "pt-BR" : "pt-BR")
                  const cw = e.currentTarget.contentWindow
                  if (cw) {
                    cw.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
                    cw.postMessage({ type: "BOLD_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
                    cw.postMessage({ type: "BOLD_WIDGET_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
                    cw.postMessage({ type: "BOLD_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
                    cw.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                    cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                    cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                    cw.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                  }
                } catch (err) {
                  // ignore
                }
              }}
            />
          </div>
        )}
      </div>
    )
  }

  const renderWidgetList = () => (
    <div className={`p-2.5 flex-1 min-h-0 min-w-0 ${side === "top" ? "flex flex-row gap-3 overflow-x-auto overflow-y-hidden w-full h-full items-start scrollbar-thin" : "flex flex-col gap-3 overflow-y-auto overflow-x-hidden w-full scrollbar-thin pb-28"}`}>
      {sideWidgets.map((wid) => {
        const pw = widgetsConfig.find((w) => w.id === wid)
        if (!pw) return null
        const isPinCalc = pw.id === "system-calculator"
        const isPinCalcFocused = calcFocusedWidgetId === wid
        const isMinimized = minimizedWidgets.has(wid)
        const def = getWidgetDefaultSize(wid, side)
        const itemWidth = widgetItemSizes[wid]?.width || def.width || 320
        const itemHeight = widgetItemSizes[wid]?.height || def.height || 300

        return (
          <div
            key={wid}
            draggable
            onDragStart={(e) => handleDragStartCard(e, wid)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropCard(e, wid)}
            style={
              side === "top"
                ? { width: isMinimized ? "38px" : `${itemWidth}px` }
                : { height: isMinimized ? "auto" : `${itemHeight}px` }
            }
            className={`relative flex flex-col rounded-2xl border overflow-hidden bg-white/90 dark:bg-zinc-950/90 shadow-sm backdrop-blur-md transition-all shrink-0 cursor-grab active:cursor-grabbing ${side === "top" ? "h-full" : "w-full"
              } ${isPinCalcFocused
                ? "border-[#004EB1] dark:border-blue-600 ring-1 ring-[#004EB1]/30"
                : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700"
              }`}
            onClick={() => {
              if (isPinCalc) setCalcFocusedWidgetId(wid)
            }}
          >
            {/* NO TOPO: Faixa Vertical na Esquerda para Máximo Destaque ao Conteúdo */}
            {side === "top" ? (
              <div className="flex flex-row h-full w-full overflow-hidden relative">
                {/* Vertical Left Strip */}
                <div className="flex flex-col justify-between items-center py-2 px-1.5 bg-zinc-100/80 dark:bg-zinc-900/80 border-r border-zinc-200/80 dark:border-zinc-800/80 select-none shrink-0 w-9">
                  <div className="flex flex-col items-center gap-2">
                    <WidgetIcon id={pw.id} icon={pw.icon} name={pw.name} size={16} className="text-zinc-800 dark:text-zinc-100 shrink-0" />
                  </div>

                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      title={minimizedWidgets.has(wid) ? "Restaurar" : "Minimizar"}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMinimizeWidget(wid)
                      }}
                      className="size-6 flex items-center justify-center rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 transition-colors cursor-pointer"
                    >
                      {minimizedWidgets.has(wid) ? <ChevronDown className="size-3.5" /> : <Minus className="size-3.5" />}
                    </button>

                    <button
                      type="button"
                      title="Alterar Posição do Widget"
                      onClick={(e) => {
                        e.stopPropagation()
                        const rect = e.currentTarget.getBoundingClientRect()
                        setPositionMenu(positionMenu?.wid === wid ? null : { wid, x: rect.right + 8, y: rect.top })
                      }}
                      className={`size-6 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${positionMenu?.wid === wid
                          ? "bg-[#004EB1] text-white"
                          : "hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400"
                        }`}
                    >
                      <Move className="size-3.5" />
                    </button>

                    <button
                      type="button"
                      title="Fechar Widget"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleFloatingWidget(wid)
                      }}
                      className="size-6 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 h-full overflow-hidden flex flex-col">
                  {renderWidgetCardContent(pw)}
                </div>

                {/* Alça de Redimensionamento Individual Horizontal (No Topo) */}
                {!isMinimized && (
                  <div
                    onMouseDown={(e) => handleItemResizeStart(e, wid)}
                    title="Arraste para ajustar a largura deste widget"
                    className="absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize hover:bg-[#004EB1]/30 transition-colors z-20 group flex items-center justify-center select-none"
                  >
                    <div className="w-0.5 h-6 rounded-full bg-zinc-300 dark:bg-zinc-750 group-hover:bg-[#004EB1] transition-colors" />
                  </div>
                )}
              </div>
            ) : (
              /* CARD PADRÃO PARA ESQUERDA E DIREITA */
              <>
                <div className="flex items-center justify-between px-3 py-2 bg-zinc-100/80 dark:bg-zinc-900/80 border-b border-zinc-200/80 dark:border-zinc-800/80 select-none shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <WidgetIcon id={pw.id} icon={pw.icon} name={pw.name} size={15} className="text-zinc-800 dark:text-zinc-100 shrink-0" />
                    <span className="text-xs font-bold text-zinc-850 dark:text-zinc-100 font-sans truncate">
                      {pw.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      title={minimizedWidgets.has(wid) ? "Restaurar" : "Minimizar"}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMinimizeWidget(wid)
                      }}
                      className="size-6 flex items-center justify-center rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 transition-colors cursor-pointer"
                    >
                      {minimizedWidgets.has(wid) ? <ChevronDown className="size-3.5" /> : <Minus className="size-3.5" />}
                    </button>

                    <button
                      type="button"
                      title="Alterar Posição do Widget"
                      onClick={(e) => {
                        e.stopPropagation()
                        const rect = e.currentTarget.getBoundingClientRect()
                        setPositionMenu(positionMenu?.wid === wid ? null : { wid, x: rect.left - 85, y: rect.bottom + 6 })
                      }}
                      className={`size-6 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${positionMenu?.wid === wid
                          ? "bg-[#004EB1] text-white"
                          : "hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400"
                        }`}
                    >
                      <Move className="size-3.5" />
                    </button>

                    <button
                      type="button"
                      title="Fechar Widget"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleFloatingWidget(wid)
                      }}
                      className="size-6 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </div>

                {renderWidgetCardContent(pw)}

                {/* Alça de Redimensionamento Individual Vertical (Nas Laterais) */}
                {!isMinimized && (
                  <div
                    onMouseDown={(e) => handleItemResizeStart(e, wid)}
                    title="Arraste para ajustar a altura deste widget"
                    className="absolute bottom-0 left-0 right-0 h-2.5 cursor-row-resize hover:bg-[#004EB1]/30 transition-colors z-20 group flex items-center justify-center select-none"
                  >
                    <div className="h-0.5 w-8 rounded-full bg-zinc-300 dark:bg-zinc-750 group-hover:bg-[#004EB1] transition-colors" />
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )

  // =========================================================
  // INLINE GROUNDED LAYOUT (FLEX INTEGRADO AO LAYOUT)
  // COM BARRA SUPERIOR PARA BOTÃO DE RECOLHER NÃO FICAR ATRÁS DE NENHUM CARD
  // =========================================================

  return (
    <>
      {side === "left" && (
        <div
          className="relative flex flex-row h-full min-h-0 border-r border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/80 dark:bg-zinc-900/80 backdrop-blur-xl shrink-0 select-none"
          style={{ width: `${safeLeftWidth}px` }}
        >
          <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
            {/* Header dedicado para o botão de recolher nunca ficar atrás dos cards */}
            <div className="px-2 py-1 flex items-center justify-end border-b border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/90 dark:bg-zinc-900/90 backdrop-blur-md shrink-0 select-none h-6">
              <button
                type="button"
                onClick={() => toggleCollapseSide("left")}
                title="Recolher Painel"
                className="px-1.5 py-0.5 rounded-lg text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer flex items-center gap-1 text-[10px] font-semibold"
              >
                <span className="text-[10px] text-zinc-400">Recolher</span>
                <ChevronLeft className="size-3.5" />
              </button>
            </div>
            {renderWidgetList()}
          </div>

          {/* Resizable Handle */}
          <div
            onMouseDown={handleResizeStart}
            title="Clique e arraste para redimensionar a largura do painel esquerdo"
            className="w-2 h-full flex items-center justify-center cursor-col-resize hover:bg-[#004EB1]/30 border-r border-white/10 group transition-colors shrink-0"
          >
            <GripVertical className="size-3 text-zinc-400 opacity-40 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}

      {side === "right" && (
        <div
          className="relative flex flex-row h-full min-h-0 border-l border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/80 dark:bg-zinc-900/80 backdrop-blur-xl shrink-0 select-none"
          style={{ width: `${safeRightWidth}px` }}
        >
          {/* Resizable Handle */}
          <div
            onMouseDown={handleResizeStart}
            title="Clique e arraste para redimensionar a largura do painel direito"
            className="w-2 h-full flex items-center justify-center cursor-col-resize hover:bg-[#004EB1]/30 border-l border-white/10 group transition-colors shrink-0"
          >
            <GripVertical className="size-3 text-zinc-400 opacity-40 group-hover:opacity-100 transition-opacity" />
          </div>

          <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
            {/* Header dedicado para o botão de recolher nunca ficar atrás dos cards */}
            <div className="px-2 py-1 flex items-center justify-start border-b border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/90 dark:bg-zinc-900/90 backdrop-blur-md shrink-0 select-none h-6">
              <button
                type="button"
                onClick={() => toggleCollapseSide("right")}
                title="Recolher Painel"
                className="px-1.5 py-0.5 rounded-lg text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer flex items-center gap-1 text-[10px] font-semibold"
              >
                <ChevronRight className="size-3.5" />
                <span className="text-[10px] text-zinc-400">Recolher</span>
              </button>
            </div>
            {renderWidgetList()}
          </div>
        </div>
      )}

      {side === "top" && (
        <div
          className="relative flex flex-col w-full border-b border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/80 dark:bg-zinc-900/80 backdrop-blur-xl shrink-0 select-none"
          style={{ height: `${safeTopPanelHeight}px` }}
        >
          {/* Botão Ícone Discreto no canto superior direito do painel superior */}
          <button
            type="button"
            onClick={() => toggleCollapseSide("top")}
            title="Recolher Painel"
            className="absolute right-2 top-2 z-30 p-1 rounded-md text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer flex items-center gap-1 text-[10px] font-semibold"
          >
            <span className="text-[10px] text-zinc-400">Recolher</span>
            <ChevronUp className="size-3.5" />
          </button>

          <div className="flex-1 flex flex-col w-full h-full min-h-0 min-w-0 overflow-hidden">
            {renderWidgetList()}
          </div>
          {/* Resizable Handle */}
          <div
            onMouseDown={handleResizeStart}
            title="Clique e arraste para redimensionar a altura do painel superior"
            className="h-2 w-full flex items-center justify-center cursor-row-resize hover:bg-[#004EB1]/30 border-b border-white/10 group transition-colors shrink-0"
          >
            <GripHorizontal className="size-3 text-zinc-400 opacity-40 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}

      {/* MENU FLUTUANTE DE POSIÇÕES COM POSITION FIXED FORA DO CONTAINER */}
      {positionMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="fixed z-[100] w-48 p-2.5 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl flex flex-col gap-1.5 animate-in fade-in duration-150 font-sans text-xs text-zinc-800 dark:text-zinc-200 select-none"
          style={{
            left: Math.max(10, Math.min(window.innerWidth - 200, positionMenu.x)),
            top: Math.max(10, Math.min(window.innerHeight - 150, positionMenu.y))
          }}
        >
          <div className="flex items-center justify-between px-1 pb-1 border-b border-zinc-200 dark:border-zinc-800">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Posição Fixada</span>
            <button
              type="button"
              onClick={() => setPositionMenu(null)}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-white cursor-pointer"
            >
              <X className="size-3" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1 pt-1">
            <button
              type="button"
              onClick={() => moveWidgetToSide(positionMenu.wid, "left")}
              className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border text-[10px] font-semibold transition-colors cursor-pointer ${(safeWidgetSides[positionMenu.wid] || "right") === "left"
                  ? "bg-[#004EB1] border-[#004EB1] text-white"
                  : "border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                }`}
            >
              <PanelLeft className="size-3.5" />
              <span>Esquerda</span>
            </button>

            <button
              type="button"
              onClick={() => moveWidgetToSide(positionMenu.wid, "top")}
              className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border text-[10px] font-semibold transition-colors cursor-pointer ${(safeWidgetSides[positionMenu.wid] || "right") === "top"
                  ? "bg-[#004EB1] border-[#004EB1] text-white"
                  : "border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                }`}
            >
              <PanelTop className="size-3.5" />
              <span>Topo</span>
            </button>

            <button
              type="button"
              onClick={() => moveWidgetToSide(positionMenu.wid, "right")}
              className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border text-[10px] font-semibold transition-colors cursor-pointer ${(safeWidgetSides[positionMenu.wid] || "right") === "right"
                  ? "bg-[#004EB1] border-[#004EB1] text-white"
                  : "border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                }`}
            >
              <PanelRight className="size-3.5" />
              <span>Direita</span>
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// =========================================================================
// COMPONENT FOR COLLAPSED EAR HANDLES (ORELHAS DISCRETAS FLUTUANTES)
// COM MAIOR TRANSPARÊNCIA
// =========================================================================

interface PinnedEarHandlesProps {
  pinnedWidgets: string[]
  widgetsConfig: any[]
  pinnedWidgetSides: Record<string, PanelSide>
  collapsedSides: Record<PanelSide, boolean>
  setCollapsedSides: React.Dispatch<React.SetStateAction<Record<PanelSide, boolean>>>
}

export function PinnedEarHandles({
  pinnedWidgets,
  widgetsConfig,
  pinnedWidgetSides,
  collapsedSides,
  setCollapsedSides
}: PinnedEarHandlesProps) {
  const [earPositions, setEarPositions] = React.useState<Record<PanelSide, number>>({
    left: 45,
    right: 45,
    top: 50
  })

  if (!pinnedWidgets || pinnedWidgets.length === 0) return null

  const handleEarMouseDown = (e: React.MouseEvent, side: PanelSide) => {
    e.preventDefault()
    e.stopPropagation()

    let hasDragged = false
    const startX = e.clientX
    const startY = e.clientY

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dist = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY)
      if (dist > 4) {
        hasDragged = true
      }

      if (side === "left" || side === "right") {
        const percentY = Math.min(85, Math.max(15, (moveEvent.clientY / window.innerHeight) * 100))
        setEarPositions((prev) => ({ ...prev, [side]: percentY }))
      } else {
        const percentX = Math.min(85, Math.max(15, (moveEvent.clientX / window.innerWidth) * 100))
        setEarPositions((prev) => ({ ...prev, top: percentX }))
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)

      if (!hasDragged && setCollapsedSides) {
        setCollapsedSides((prev) => ({ ...prev, [side]: !prev?.[side] }))
      }
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  const safeWidgetSides = pinnedWidgetSides || {}
  const safeCollapsedSides = collapsedSides || { left: false, right: false, top: false }

  const configuredSides = Object.values(safeWidgetSides)
  const defaultSide: PanelSide = configuredSides.length > 0 ? configuredSides[configuredSides.length - 1] : "right"

  return (
    <>
      {(["left", "right", "top"] as PanelSide[]).map((side) => {
        if (!safeCollapsedSides[side]) return null

        const sideWidgets = (pinnedWidgets || []).filter((wid) => (safeWidgetSides[wid] || defaultSide) === side)
        if (sideWidgets.length === 0) return null

        const earPos = earPositions[side]

        if (side === "left") {
          return (
            <div
              key={side}
              style={{ top: `${earPos}%` }}
              onMouseDown={(e) => handleEarMouseDown(e, "left")}
              title="Clique para expandir ou arraste para reposicionar o painel da Esquerda"
              className="fixed left-0 -translate-y-1/2 z-[50] bg-zinc-950/30 hover:bg-zinc-950/65 opacity-50 hover:opacity-100 backdrop-blur-md border-r border-y border-white/10 hover:border-white/30 text-white rounded-r-xl px-2 py-2 shadow-xl flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing transition-all duration-200 hover:scale-105 group select-none animate-in fade-in"
            >
              <div className="flex flex-col items-center gap-1.5 py-0.5">
                {sideWidgets.map((wid) => {
                  const w = widgetsConfig.find((wc) => wc.id === wid)
                  return (
                    <div key={wid} className="transition-transform group-hover:scale-110 flex items-center justify-center">
                      <WidgetIcon id={wid} icon={w?.icon} name={w?.name} size={15} className="text-white shrink-0" />
                    </div>
                  )
                })}
              </div>
              <ChevronRight className="size-3 text-blue-400 group-hover:translate-x-0.5 transition-transform" />
            </div>
          )
        }

        if (side === "right") {
          return (
            <div
              key={side}
              style={{ top: `${earPos}%` }}
              onMouseDown={(e) => handleEarMouseDown(e, "right")}
              title="Clique para expandir ou arraste para reposicionar o painel da Direita"
              className="fixed right-0 -translate-y-1/2 z-[50] bg-zinc-950/30 hover:bg-zinc-950/65 opacity-50 hover:opacity-100 backdrop-blur-md border-l border-y border-white/10 hover:border-white/30 text-white rounded-l-xl px-2 py-2 shadow-xl flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing transition-all duration-200 hover:scale-105 group select-none animate-in fade-in"
            >
              <div className="flex flex-col items-center gap-1.5 py-0.5">
                {sideWidgets.map((wid) => {
                  const w = widgetsConfig.find((wc) => wc.id === wid)
                  return (
                    <div key={wid} className="transition-transform group-hover:scale-110 flex items-center justify-center">
                      <WidgetIcon id={wid} icon={w?.icon} name={w?.name} size={15} className="text-white shrink-0" />
                    </div>
                  )
                })}
              </div>
              <ChevronLeft className="size-3 text-blue-400 group-hover:-translate-x-0.5 transition-transform" />
            </div>
          )
        }

        // Top Ear
        return (
          <div
            key={side}
            style={{ left: `${earPos}%` }}
            onMouseDown={(e) => handleEarMouseDown(e, "top")}
            title="Clique para expandir ou arraste para reposicionar o painel do Topo"
            className="fixed top-[100px] -translate-x-1/2 z-[50] bg-zinc-950/30 hover:bg-zinc-950/65 opacity-50 hover:opacity-100 backdrop-blur-md border-x border-b border-t-0 border-white/10 hover:border-white/30 text-white rounded-b-xl px-3 py-1.5 shadow-xl flex items-center gap-2.5 cursor-grab active:cursor-grabbing transition-all duration-200 hover:scale-105 group select-none animate-in fade-in"
          >
            <div className="flex items-center gap-2">
              {sideWidgets.map((wid) => {
                const w = widgetsConfig.find((wc) => wc.id === wid)
                return (
                  <div key={wid} className="transition-transform group-hover:scale-110 flex items-center justify-center">
                    <WidgetIcon id={wid} icon={w?.icon} name={w?.name} size={15} className="text-white shrink-0" />
                  </div>
                )
              })}
            </div>
            <ChevronDown className="size-3 text-blue-400 group-hover:translate-y-0.5 transition-transform" />
          </div>
        )
      })}
    </>
  )
}
